<template>
	<div
		id="attending-container"
		class="w-full flex-1 px-4"
	>
		<div
			id="attending-badge"
			:class="selectedAttending.category"
		>
			<span v-html="ParseTicket(selectedAttending)"></span>
		</div>
		<div id="attending-data">
			<div class="attending-data-group date">
				<label>DATA:</label>
				<span>{{ selectedAttending.date }}</span>
			</div>
			<div class="attending-data-group time">
				<label>HORA:</label>
				<span>{{ selectedAttending.time }}</span>
			</div>
			<div class="attending-data-group name">
				<label>NOME:</label>
				<span
					:class="{
						unknown: selectedAttending.patient.unknown,
					}"
				>
					{{
						selectedAttending.patient.unknown
							? 'DESCONHECIDO'
							: selectedAttending.patient.name
					}}
				</span>
			</div>
			<div class="attending-data-group doc_id">
				<template v-if="selectedAttending.patient.unknown">
					<label>ID:</label>
					<span class="unknown"> DESCONHECIDO </span>
				</template>
				<template v-else>
					<label>{{ selectedAttending.patient.doc_id.tipo }}:</label>
					<span> {{ selectedAttending.patient.doc_id.numero }} </span>
				</template>
			</div>
		</div>
		<div id="attending-flows-list">
			<div id="highlight-flows-container">
				<button
					v-for="flow in highlightAttendingFlows"
					:key="flow.id"
					:id="`flow-${flow.id}`"
					class="attending-flow"
					:class="{
						emergency: isEmergency && flow.id === EMERGENCY_FLOW_ID,
					}"
					@click="OpenFlow(flow, $event)"
				>
					<template v-if="flow.icon === 'fa-circle-exclamation'">
						<span class="icon fa-circle-exclamation">i</span>
					</template>
					<template v-else>
						<i
							class="flow-icon"
							:class="`fal ${flow.icon}`"
						></i>
					</template>
					<span class="flow-label">{{ flow.label }}</span>
				</button>
			</div>
			<div id="not-highlight-flows-container">
				<button
					v-for="flow in notHighlightAttendingFlows"
					:key="flow.id"
					:id="`flow-${flow.id}`"
					class="attending-flow"
					@click="OpenFlow(flow, $event)"
				>
					<template v-if="flow.icon === 'fa-circle-exclamation'">
						<span class="icon fa-circle-exclamation">i</span>
					</template>
					<template v-else>
						<i
							class="flow-icon"
							:class="`fal ${flow.icon}`"
						></i>
					</template>
					<span class="flow-label">{{ flow.label }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
	import { RECEPTIONS_ATTENDANCE_FLOWS } from '@/constants'
	import Heartbeat from '@/utils/heartbeat/heartbeat.js'

	export default {
		name: 'Attending',
		props: {
			selectedAttending: {
				type: Object,
				required: true,
			},
		},
		data() {
			return {
				ATTENDING_FLOWS: [...RECEPTIONS_ATTENDANCE_FLOWS],
				EMERGENCY_FLOW_ID: 1,
				receptionFlowMessageHandler: null,
			}
		},
		computed: {
			isEmergency() {
				return this.selectedAttending.category === 'emergency'
			},
			highlightAttendingFlows() {
				return RECEPTIONS_ATTENDANCE_FLOWS.filter(flow => {
					if (!flow.highlightTypes) return false
					if (this.isEmergency && flow.id === this.EMERGENCY_FLOW_ID)
						return true

					return flow.highlightTypes.includes(
						this.selectedAttending.type.value
					)
				})
			},
			notHighlightAttendingFlows() {
				return RECEPTIONS_ATTENDANCE_FLOWS.filter(flow => {
					if (!flow.highlightTypes) return true
					if (flow.id === this.EMERGENCY_FLOW_ID) return false

					return !flow.highlightTypes.includes(
						this.selectedAttending.type.value
					)
				})
			},
		},
		methods: {
			OpenFlow(flow) {
				const query = {
					flow_type: flow.key,
				}

				if (this.selectedAttending) {
					query.temp_attendance_id =
						this.selectedAttending.temp_attendance_id
					query.patient_id = this.selectedAttending.patient.id
				}

				const { href } = this.$router.resolve({
					name: 'ReceptionFlow',
					query,
				})

				const tab = window.open(href, '_blank')
				this.SetupReceptionFlowListener(tab)

				this.$emit('open-flow')
			},
			SetupReceptionFlowListener(tab) {
				this.ClearReceptionFlowListener()

				this.receptionFlowMessageHandler = event =>
					this.onMessage(event, tab)
				window.addEventListener(
					'message',
					this.receptionFlowMessageHandler
				)
			},
			ClearReceptionFlowListener() {
				if (!this.receptionFlowMessageHandler) return

				window.removeEventListener(
					'message',
					this.receptionFlowMessageHandler
				)
				this.receptionFlowMessageHandler = null
			},
			ParseTicket(item) {
				return item.ticket.toUpperCase()
			},
			async onMessage(event, tab) {
				if (event.origin !== window.location.origin) return

				if (event.data?.type?.startsWith('RECEPTION_FLOW_')) {
					this.ClearReceptionFlowListener()
					tab?.close()
				}
			},
		},
		mounted() {
			window.Heartbeat = new Heartbeat()
		},
		beforeDestroy() {
			window.Heartbeat.CloseConnection().then(() => {
				window.Heartbeat = null
			})

			this.ClearReceptionFlowListener()
		},
	}
</script>

<style lang="scss" scoped>
	#attending-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		overflow: hidden;

		#attending-badge {
			font-family: 'Inter', sans-serif;
			height: 4rem;
			width: 100%;
			padding: 1rem 2rem;
			border-radius: 0.5rem 0.5rem 0 0;
			font-size: 2.25rem;
			line-height: 2.5rem;
			letter-spacing: -0.025em;
			text-align: center;
			font-weight: 900;
			display: flex;
			justify-content: center;
			align-items: center;
			background-color: var(--conventionalBackgroundColor);
			color: var(--conventionalColor);

			&.emergency {
				background-color: var(--emergencyActiveBackgroundColor);
				color: var(--emergencyBackgroundColor);
			}

			&.priority {
				background-color: var(--priorityBackgroundColor);
				color: var(--priorityColor);
			}
		}

		#attending-data {
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 2rem;

			.attending-data-group {
				display: flex;
				align-items: center;
				gap: 0.5rem;
				font-size: 1.65rem;

				&.name {
					max-width: calc(80vw - (14vw + 10vw + 22vw) - 3rem);
					flex-grow: 1;
				}
				label {
					margin: 0;
					font-weight: normal;
					color: var(--defaultFontColor);
				}

				span {
					font-weight: 900;
					color: var(--fontColor);
					white-space: nowrap;
					text-overflow: ellipsis;
					overflow: hidden;
					text-wrap: nowrap;

					&.unknown {
						font-weight: 500;
						opacity: 0.5;
						color: var(--defaultFontColor);
					}
				}
			}
		}

		#attending-flows-list {
			width: 100%;
			height: 100%;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			gap: 1.5rem;
			padding: 1rem 0;

			#highlight-flows-container {
				display: flex;
				justify-content: center;
				align-items: center;
				gap: 1rem;

				.attending-flow {
					width: 10rem;
					height: 10rem;

					.flow-icon {
						font-size: 3rem;
					}

					.flow-label {
						font-size: 16px;
					}
				}
			}

			#not-highlight-flows-container {
				display: flex;
				justify-content: center;
				align-items: center;
				gap: 1rem;
			}

			.attending-flow {
				width: 8rem;
				height: 8rem;
				background-color: var(--newPrimaryBackgroundColor);
				border: 1px solid var(--newPrimaryBackgroundColor);
				color: var(--newPrimaryColor);
				border-radius: 0.5rem;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 0.5rem;

				.flow-icon {
					font-size: 2.5rem;
					font-weight: 500;
				}

				.flow-label {
					font-size: 13px;
					font-weight: 900;
				}

				&:hover {
					background-color: var(--newPrimaryHoverBackgroundColor);
					border-color: var(--newPrimaryHoverBackgroundColor);
					color: var(--newPrimaryHoverColor);

					.icon {
						color: var(--newPrimaryHoverColor);
						border-color: var(--newPrimaryHoverColor);
					}
				}

				&:active,
				&.selected {
					background-color: var(--newPrimaryActiveBackgroundColor);
					border-color: var(--newPrimaryActiveBackgroundColor);
					color: var(--newPrimaryActiveColor);

					.icon {
						color: var(--newPrimaryActiveColor);
						border-color: var(--newPrimaryActiveColor);
					}
				}

				&.emergency {
					background-color: var(--emergencyBackgroundColor);
					border-color: var(--emergencyBackgroundColor);
					color: var(--emergencyColor);

					&:hover {
						background-color: var(--emergencyHoverBackgroundColor);
						border-color: var(--emergencyHoverBackgroundColor);
					}
					&:active,
					&.selected {
						background-color: var(--emergencyActiveBackgroundColor);
						border-color: var(--emergencyActiveBackgroundColor);
					}
				}
			}
		}
	}

	.fa-circle-exclamation {
		width: 2.5rem;
		height: 2.5rem;
		border-radius: 50%;
		border-width: 3px;
		border-style: solid;
		border-color: var(--newPrimaryColor);
		font-size: 1.5rem;
		font-weight: 700;
	}
</style>
